import React from 'react';
import { Box, ButtonBase, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

import type { AssetSummary } from '../../domain';
import { formatDimensions, formatFileSize } from './assetUtils';
import { lifecycleMeta } from './lifecycleUtils';
import { documentSnippet, isDocumentAsset } from './previewHelpers';

interface Props {
  asset: AssetSummary;
  onClick: (asset: AssetSummary) => void;
  isFavorited?: boolean;
  onToggleFavorite?: (asset: AssetSummary) => void;
}

/**
 * Single asset thumbnail card. Lazy-loads the image; falls back to a
 * placeholder icon when the URL fails or is missing. Click opens the
 * preview modal. A favorite heart overlays the top-right (Slice 8) and a
 * lifecycle chip sits in the footer (Slice 9).
 */
export const AssetCard: React.FC<Props> = ({
  asset,
  onClick,
  isFavorited = false,
  onToggleFavorite,
}) => {
  const [imgError, setImgError] = React.useState(false);
  const isDocument = isDocumentAsset(asset);
  // Prefer the HMAC-signed companion URL so the browser can load the image
  // without an `x-user-id` header (native `<img>` requests can't attach
  // custom headers). See qa/bugs/2026-05-25-asset-files-401-canvas-render.md.
  const previewUrl = asset.signedThumbnailUrl ?? asset.thumbnailUrl ?? asset.signedUrl ?? asset.url;
  const snippet = isDocument ? documentSnippet(asset, 100) : '';
  const meta = [formatDimensions(asset.width, asset.height), formatFileSize(asset.sizeBytes)]
    .filter(Boolean)
    .join(' · ');
  const life = asset.lifecycleState ? lifecycleMeta(asset.lifecycleState) : null;

  return (
    <ButtonBase
      onClick={() => onClick(asset)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        borderRadius: 1,
        border: theme => `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '&:hover': {
          borderColor: 'primary.main',
        },
      }}
      aria-label={asset.promptUsed ?? asset.assetId}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {isDocument ? (
          <Stack
            spacing={0.75}
            sx={{ width: '100%', height: '100%', p: 1.25, justifyContent: 'flex-start' }}
            aria-label="Document preview"
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 28, opacity: 0.5 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.35,
              }}
            >
              {snippet || 'Empty document'}
            </Typography>
          </Stack>
        ) : previewUrl && !imgError ? (
          <Box
            component="img"
            src={previewUrl}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <ImageOutlinedIcon sx={{ fontSize: 40, opacity: 0.3 }} />
        )}

        {onToggleFavorite && (
          <Tooltip title={isFavorited ? 'Unfavorite' : 'Favorite'}>
            {/* component="span" so this isn't a <button> nested inside the
                card's ButtonBase (invalid HTML). */}
            <IconButton
              component="span"
              size="small"
              aria-label={isFavorited ? 'Unfavorite asset' : 'Favorite asset'}
              onClick={e => {
                e.stopPropagation();
                onToggleFavorite(asset);
              }}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(0,0,0,0.45)',
                color: isFavorited ? 'error.main' : 'common.white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
              }}
            >
              {isFavorited ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ p: 1, minHeight: 56 }}>
        <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 500 }}>
          {asset.promptUsed?.trim() || asset.subType || 'asset'}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
          {meta && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              {meta}
            </Typography>
          )}
          {life && (
            <Chip
              label={life.label}
              color={life.color}
              size="small"
              variant="outlined"
              sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
            />
          )}
        </Stack>
      </Box>
    </ButtonBase>
  );
};
